import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { CommunicationRecipientDto } from '../dtos/communication.dto';
import { getSectionStyles, getRecipientStyles } from '../shared';

interface RecipientsCardProps {
  recipients: CommunicationRecipientDto[];
  selectedRecipientIds: Set<string>;
  onRecipientToggle: (recipientId: string) => void;
  onRemoveRecipient: (recipientId: string) => void;
  showAddRecipient: boolean;
  onShowAddRecipient: (show: boolean) => void;
  newRecipientName: string;
  newRecipientEmail: string;
  newRecipientPhone: string;
  onNewRecipientNameChange: (value: string) => void;
  onNewRecipientEmailChange: (value: string) => void;
  onNewRecipientPhoneChange: (value: string) => void;
  onAddRecipient: () => void;
  onCancelAddRecipient: () => void;
}

export const RecipientsCard: React.FC<RecipientsCardProps> = ({
  recipients,
  selectedRecipientIds,
  onRecipientToggle,
  onRemoveRecipient,
  showAddRecipient,
  onShowAddRecipient,
  newRecipientName,
  newRecipientEmail,
  newRecipientPhone,
  onNewRecipientNameChange,
  onNewRecipientEmailChange,
  onNewRecipientPhoneChange,
  onAddRecipient,
  onCancelAddRecipient,
}) => {
  const theme = useTheme();
  const sectionStyles = getSectionStyles(theme);
  const recipientStyles = getRecipientStyles(theme);
  
  const [isExpanded, setIsExpanded] = React.useState(false);

  return (
    <Paper sx={sectionStyles.paper} elevation={0} variant="outlined">
      <Box
        sx={{ ...sectionStyles.header, cursor: 'pointer' }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Recipients
          <Chip
            label={selectedRecipientIds.size}
            size="small"
            color="primary"
            sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
          />
        </Typography>
        {/* We set padding to 0 so the icon doesn't expand the header vertically */}
        <IconButton size="small" sx={{ padding: 0 }}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      
      {/* Recipient list - collapsible */}
      <Collapse in={isExpanded} timeout={150}>
        <Stack spacing={1} sx={sectionStyles.content}>
          {recipients.map(recipient => (
            <Box
              key={recipient.id}
              sx={{
                ...recipientStyles.item,
                ...(selectedRecipientIds.has(recipient.id) ? recipientStyles.itemSelected : {}),
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedRecipientIds.has(recipient.id)}
                    onChange={() => onRecipientToggle(recipient.id)}
                    size="small"
                  />
                }
                label={
                  <Box sx={recipientStyles.info}>
                    <Box sx={recipientStyles.nameRow}>
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="body2" component="span">
                        {recipient.name || 'Unnamed'}
                      </Typography>
                      {recipient.isPrimary && (
                        <Chip label="Primary" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                      )}
                      {recipient.isNew && (
                        <Chip label="New" size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
                      )}
                    </Box>
                    <Box sx={recipientStyles.contactRow}>
                      {recipient.email && (
                        <Box sx={recipientStyles.contactChip}>
                          <EmailIcon fontSize="small" />
                          <Typography variant="caption">{recipient.email}</Typography>
                        </Box>
                      )}
                      {recipient.phoneNumber && (
                        <Box sx={recipientStyles.contactChip}>
                          <PhoneIcon fontSize="small" />
                          <Typography variant="caption">{recipient.phoneNumber}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                }
                sx={{ flex: 1, m: 0, alignItems: 'flex-start' }}
              />
              {recipient.isNew && (
                <IconButton
                  size="small"
                  onClick={() => onRemoveRecipient(recipient.id)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Stack>
      </Collapse>

      {/* Add Additional Recipient button - always visible */}
      <Box sx={{ p: 2, pt: isExpanded ? 0 : 2 }}>
        {!showAddRecipient && (
          <Button
            startIcon={<AddIcon />}
            size="small"
            onClick={() => onShowAddRecipient(true)}
            sx={{ borderStyle: 'dashed' }}
            variant="outlined"
          >
            Add Additional Recipient
          </Button>
        )}

        {/* Add Recipient Form */}
        <Collapse in={showAddRecipient} timeout={150}>
          <Box sx={recipientStyles.addForm}>
            <Typography variant="subtitle2" gutterBottom>
              Add New Recipient
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Name (optional)"
                size="small"
                fullWidth
                value={newRecipientName}
                onChange={e => onNewRecipientNameChange(e.target.value)}
                autoComplete="off"
              />
              <TextField
                label="Email"
                size="small"
                fullWidth
                type="email"
                value={newRecipientEmail}
                onChange={e => onNewRecipientEmailChange(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Phone Number"
                size="small"
                fullWidth
                value={newRecipientPhone}
                onChange={e => onNewRecipientPhoneChange(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={recipientStyles.addActions}>
                <Button type="button" size="small" onClick={onCancelAddRecipient}>
                  Cancel
                </Button>
                <Button type="button" size="small" variant="contained" onClick={onAddRecipient}>
                  Add
                </Button>
              </Box>
            </Stack>
          </Box>
        </Collapse>
      </Box>
    </Paper>
  );
};
